import { FC, ReactNode, useEffect, useMemo, useRef, useCallback } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { Loader } from 'rsuite'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'

import useUrls from '../../../services/useUrls'
import { useApi } from '../../../services/useApi'
import useCategoryDataStructure from '../../hooks/useCategoryDataStructure'
import BaseFilterPicker from './BaseFilterPicker'
import { createUrlParams } from '../../../lib/params'
import { formatHighlightedTag } from '../../../lib/resources/attribute'
import { getParentNodes } from '../../../lib/catalogFilter'
import { getPickerValue, handlePickerChange } from './util'
import { QueryParam } from '../../../utils/constants'
import { ProductAttribute } from '../../../utils/types/Product'
import { FilterItem, isDefined } from '../../../utils/types/misc'
import CheckboxFilterMobile from '../CheckboxFilterMobile'
import useIsOnMobile from '../../../services/useIsOnMobile'
import useGlobalAnalyticsParams from '../../hooks/useGlobalAnalyticsParams'

import styles from '../../../styles/CatalogFilters.module.less'

interface TestResultPickerProps {
  title: string
  shouldCloseFilters: boolean
  categoryCode: string
  baseStandardCode: string
  testFilter?: string
  appliedFilters?: Record<string, FilterItem[]>
  updateAppliedFilter?: (filterKey: string, filterItem: FilterItem) => void
  removeAppliedFilter?: (filterKey: string) => void
  removeAppliedFilterItem?: (filterKey: string, value: string | number) => void
}

const TestResultPicker: FC<TestResultPickerProps> = (props) => {
  const {
    title,
    shouldCloseFilters,
    categoryCode,
    baseStandardCode,
    testFilter,
    appliedFilters = {},
    updateAppliedFilter,
    removeAppliedFilter,
    removeAppliedFilterItem,
  } = props

  const { pushT } = useUrls()
  const { query, asPath } = useRouter()
  const isOnMobile = useIsOnMobile()
  const selectedSupplier = query[QueryParam.supplierCode] as string | undefined
  const { getResource } = useApi(selectedSupplier || 'default')
  const { subCategory, selectedCategory } = useCategoryDataStructure()
  const initializedRef = useRef(false)
  const { t } = useTranslation('products')
  const [globalAnalyticsParams, setGlobalAnalyticsParams] = useGlobalAnalyticsParams()
  const { filterInteractionAmount = 0 } = globalAnalyticsParams
  const categories = getParentNodes(subCategory, selectedCategory)
  const isCorrectCategory = categories.map((category) => category.code).includes(categoryCode)

  const label = t(title)

  const filterCode = `f_${baseStandardCode}${testFilter ?? ''}`

  // Get values from query params (for initial load and URL-based navigation)
  const queryParamValues = useMemo(
    () => getPickerValue(query, filterCode),
    [query, filterCode],
  )

  // Use appliedFilters for controlling the component if provided,
  // otherwise fall back to query params
  const pickerValue = useMemo(() => {
    // If appliedFilters for this filter exist, use them
    if (appliedFilters[filterCode]?.length) {
      return appliedFilters[filterCode].map((item) => item.value as string)
    }
    return queryParamValues
  }, [appliedFilters, filterCode, queryParamValues])

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: ['standards.test'],
  })
  const {
    items,
    refine,
    searchForItems,
    isFromSearch,
  } = useRefinementList({
    attribute: 'standards.test',
    operator: 'or',
    limit: 100,
  })
  if (testFilter && !isFromSearch) {
    searchForItems(testFilter)
  }

  const {
    data: attributes,
    isLoading,
  } = getResource<ProductAttribute>(isCorrectCategory ? 'attribute' : null, createUrlParams({
    include: 'catalog',
    filter: {
      '&&': [
        {
          '==': {
            field: 'attribute.type',
            value: 'standards-test',
          },
        },
        {
          '=~': {
            field: 'attribute.code',
            value: baseStandardCode,
          },
        },
        ...(testFilter ? [{
          '=~': {
            field: 'attribute.label',
            value: testFilter,
          },
        }] : []),
      ],
    },
    sort: 'attribute.code',
    page: { limit: '20' },
  }))

  const data = attributes
    .filter((attr) => (
      items.find((item) => item.label === attr['attribute.label'])
    ))
    .map((attr) => {
      const levelLabel = formatHighlightedTag(attr['attribute.label'])
      const count = items.find((item) => item.label === attr['attribute.label'])?.count || 0
      return {
        label: (
          <>
            {levelLabel}
            <span className={`${styles.count} ${!count ? styles.disabled : ''}`}>
              {count}
            </span>
          </>
        ),
        value: attr.id,
      }
    })

  const handleInitializeFilters = (itemIds: string[]) => {
    if (itemIds.length && updateAppliedFilter) {
      itemIds.forEach((itemId) => {
        const attribute = attributes.find((a) => a.id === itemId)
        if (attribute) {
          updateAppliedFilter(filterCode, {
            label: formatHighlightedTag(attribute['attribute.label']) || '',
            value: itemId,
          })
        }
      })
    }
  }

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refined = items.filter((item) => item.isRefined)
    if (!initializedRef.current && refined.length === 0 && pickerValue.length !== 0) {
      initializedRef.current = true
      attributes
        .filter((attribute) => pickerValue.includes(attribute.id))
        .map((attribute) => attribute['attribute.label'])
        .filter(isDefined)
        .forEach((item) => {
          refine(item)
        })
    }
  }, [pickerValue, refine])

  // Initialize appliedFilters state from URL on page load
  useEffect(() => {
    if (query && queryParamValues.length > 0) {
      handleInitializeFilters(queryParamValues)
    }
  }, [query, queryParamValues])

  const handleOnChange = useCallback((value: string[]) => {
    initializedRef.current = true
    const isClear = value.length < pickerValue.length
    const isNew = (id: string) => !pickerValue.includes(id)

    if (isClear) {
      clear()
      // Find which items were removed
      const removedItemIds = pickerValue.filter((id) => !value.includes(id))

      // Update applied filters state for removed items
      if (removeAppliedFilterItem) {
        removedItemIds.forEach((itemId) => {
          removeAppliedFilterItem(filterCode, itemId)
        })
      }
    }

    setGlobalAnalyticsParams({
      ...globalAnalyticsParams,
      selectedFilterValue: value.join('|').toLowerCase(),
      selectedFilterCode: filterCode,
      filterInteractionAmount: Number(filterInteractionAmount) + 1,
    })

    attributes
      .filter((attribute) => (
        value.includes(attribute.id) && (isClear || isNew(attribute.id))))
      .map((attribute) => attribute['attribute.label'])
      .map((a) => a)
      .filter(isDefined)
      .forEach(refine)

    // Update appliedFilters state with new structure
    if (updateAppliedFilter) {
      // For newly added items
      const newlyAdded = value.filter(isNew)
      handleInitializeFilters(newlyAdded)
    } else if (removeAppliedFilter && value.length === 0) {
      // If all items are deselected, remove entire filter key
      removeAppliedFilter(filterCode)
    }

    handlePickerChange(pushT, query, asPath, filterCode)(value)
  }, [
    query,
    asPath,
    filterCode,
    pickerValue,
    globalAnalyticsParams,
    filterInteractionAmount,
    attributes,
  ])

  const renderMenu = (menu: ReactNode) => {
    if (isLoading) {
      return (
        <Loader
          className={`${styles.loader}`}
          content="Loading..."
        />
      )
    }
    return menu
  }
  // Only render this picker if in correct category is selected
  if (!isCorrectCategory) {
    return null
  }

  if (isOnMobile) {
    return (
      <CheckboxFilterMobile
        data={data}
        value={pickerValue}
        onChange={handleOnChange}
        onSearch={searchForItems}
      />
    )
  }

  return (
    <BaseFilterPicker
      data={data}
      filterCode={filterCode}
      value={pickerValue || undefined}
      label={pickerValue.length ? t(label) : undefined}
      placeholder={t(label)}
      isSort={false}
      onChange={handleOnChange}
      cleanable={false}
      shouldCloseFilters={shouldCloseFilters}
      renderMenu={renderMenu}
    />
  )
}

export default TestResultPicker
